import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const coverUrl = (id) => id ? `${BACKEND_URL}/api/proxy-image/${id}` : null;

const DownloadPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/download/${token}`)
      .then(res => { setData(res.data); setLoading(false); })
      .catch(err => {
        const status = err.response?.status;
        const detail = err.response?.data?.detail || '';
        if (status === 410 && detail.toLowerCase().includes('expired')) {
          setError('Link do pobrania wygasł. Skontaktuj się z nami, aby otrzymać nowy link.');
        } else if (status === 410 && detail.toLowerCase().includes('limit')) {
          setError('Osiągnięto limit pobrań. Skontaktuj się z nami, jeśli potrzebujesz ponownego dostępu.');
        } else if (status === 404) {
          setError('Nieprawidłowy link do pobrania.');
        } else {
          setError('Wystąpił błąd. Spróbuj ponownie lub skontaktuj się z nami.');
        }
        setLoading(false);
      });
  }, [token]);

  // Audio files carry a ready presigned R2 url; PDFs go through the Drive proxy.
  const fileUrl = (file) =>
    file.url ? file.url : `${API}/proxy-download/${file.file_id}?filename=${encodeURIComponent(file.name)}&token=${encodeURIComponent(token)}`;

  const downloadFile = (file) => {
    window.location.href = fileUrl(file);
  };

  const downloadAll = () => {
    data?.files?.forEach((file, i) => {
      if (!file.file_id && !file.url) return;
      setTimeout(() => { window.location.href = fileUrl(file); }, i * 3000);
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-white text-xl">Ładowanie...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center p-8">
          <div className="text-red-500 text-xl mb-4">{error}</div>
          <Link to="/" className="text-emerald-500 hover:text-emerald-400">Wróć na stronę główną</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] py-16 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-[#111] rounded-2xl p-8 border border-[#222]">
          <h1 className="text-3xl font-bold text-white mb-2">Twoje zakupy</h1>
          <p className="text-gray-400 mb-8">Dziękujemy za zakup! Kliknij poniżej, aby pobrać swoje pliki.</p>

          {data?.order && (
            <div className="mb-6 p-4 bg-[#0a0a0a] rounded-lg border border-[#222]">
              <p className="text-gray-400 text-sm">Zamówienie: <span className="text-white">{data.order.order_number}</span></p>
              <p className="text-gray-400 text-sm">Email: <span className="text-white">{data.order.email}</span></p>
            </div>
          )}

          {/* Download all button — shown when 2+ files */}
          {data?.files?.length >= 2 && (
            <button
              onClick={downloadAll}
              className="w-full mb-6 py-3 px-6 bg-emerald-500 hover:bg-emerald-400 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Pobierz wszystkie ({data.files.length})
            </button>
          )}

          <div className="space-y-3">
            {data?.files?.map((file, index) => {
              if (!file.file_id && !file.url) return null;
              const imgUrl = file.cover_url || coverUrl(file.cover_image_id);
              const isAudio = file.kind === 'audio';
              return (
                <button
                  key={index}
                  onClick={() => downloadFile(file)}
                  className="w-full flex items-center gap-4 sm:gap-5 p-4 sm:p-5 bg-emerald-500/[0.06] border border-emerald-500/25 rounded-2xl hover:bg-emerald-500/10 transition-colors group text-left"
                >
                  {/* Cover sits in a dark frame whose colour matches the covers' own (near-black)
                      background, so the cover blends into the frame with no visible black box —
                      works for every cover (portrait book, phone mockup, composite) untouched. */}
                  <div className="w-24 sm:w-28 flex-shrink-0 rounded-xl bg-[#0b0f0e] border border-white/[0.06] p-2 shadow-lg shadow-black/40">
                    {imgUrl ? (
                      <img src={imgUrl} alt={file.name} className="block w-full h-auto rounded-md" onError={e => { e.target.style.display='none'; }} />
                    ) : (
                      <div className="w-full aspect-[3/4] flex items-center justify-center">
                        <svg className="w-9 h-9 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  {/* Name + format + action stacked so the title keeps full width and never
                      wraps mid-word on narrow phones */}
                  <span className="flex-1 min-w-0">
                    <span className="block text-white font-bold text-base sm:text-lg leading-snug">{file.name}</span>
                    <span className="block text-[#9CA3AF] text-sm mt-2">{isAudio ? '🎧 Audiobook (MP3)' : '📄 E-book (PDF)'}</span>
                    <span className="inline-block mt-3 bg-emerald-500 group-hover:bg-emerald-400 text-white font-semibold text-sm px-5 py-2.5 rounded-xl transition-colors">Pobierz →</span>
                  </span>
                </button>
              );
            })}
          </div>

          {data?.files?.length === 0 && (
            <div className="text-center text-gray-400 py-8">Brak plików do pobrania</div>
          )}

          <div className="mt-8 pt-8 border-t border-[#222] text-center">
            <Link to="/" className="text-emerald-500 hover:text-emerald-400">← Wróć na stronę główną</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DownloadPage;
